import { withErrorBoundary } from "@sentry/nextjs";
import { gql } from "graphql-request";
import { VFC } from "react";
import styled from "styled-components";
import { ArrowLink, LegacyImage, Markdown, Modal } from "components";
import { getImageFormat } from "lib/utils";
import { ModalContent } from "types/graphql/generated";
interface Props {
  className?: string;
  content: ModalContent;
  isOpen: boolean;
  onClose: () => void;
}

const ModalContactInfo: VFC<Props> = withErrorBoundary(
  ({
    isOpen,
    onClose,
    className = "",
    content: {
      title,
      subTitle,
      link,
      linkDescription,
      linkLabel,
      footer1ModalContacts,
      icon: {
        data: {
          icon: { ext, ...icon },
        },
      },
    },
  }) => {
    return (
      <StyledBaseModal
        isOpen={isOpen}
        onClose={onClose}
        ariaLabel="Contact us info"
        withCloseButton
        className={className}
      >
        <StyledContactModal>
          <div className="Modal__container Text_primary">
            <div className="Modal__head">
              <div>
                <h2 className="Font_32_40_600">{title}</h2>
                <p className="Modal__subTitle">{subTitle}</p>
              </div>
            </div>
            <div className="Modal__linkContainer">
              <LegacyImage
                {...icon}
                layout="fill"
                transform={{ format: getImageFormat(ext) }}
                className="Modal__icon"
              />
              <div className="Modal__description">
                <ArrowLink href={link} className="Modal__arrowLink Font_18_26_600">
                  {linkLabel}
                </ArrowLink>
                <p>{linkDescription}</p>
              </div>
            </div>
            <ul className="Modal__contactsList">
              {footer1ModalContacts.map(({ id, contact, email, phoneNumber }) => (
                <li className="Modal__contactContainer" key={id}>
                  <p className="Font_18_26_700">{contact}</p>
                  <div className="Modal__contacts">
                    <Markdown>{phoneNumber}</Markdown>
                    <Markdown className="Modal__contactEmail">{email}</Markdown>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </StyledContactModal>
      </StyledBaseModal>
    );
  },
  {}
);

const StyledBaseModal = styled(Modal)`
  @media (max-width: 500px) {
    .Modal__window {
      border-radius: 0;
    }

    .Modal__PRC {
      height: 92vh;
    }
  }
`;

const StyledContactModal = styled.div`
  color: ${({ theme }) => theme.colors.fog["80"]};
  background: ${({ theme }) => theme.colors.fog["00"]};
  padding: 40px 32px;
  max-width: 100%;
  width: 700px;

  .Modal__overlay {
    background: #000;
    opacity: 0.64;
  }

  .Modal__head {
    display: flex;
    justify-content: space-between;
    align-items: start;
  }

  .Modal__subTitle {
    margin-top: 8px;
  }

  .Modal__closeButton {
    cursor: pointer;
  }

  .Modal__closeButtonIcon {
    cursor: pointer;
    width: 20px;
    height: 20px;
    fill: ${({ theme }) => theme.colors.fog["80"]};
  }

  .Modal__linkContainer {
    margin-top: 32px;
    background: ${({ theme }) => theme.colors.eucalyptus["30"]};
    padding: 24px;
    border-radius: 8px;
    display: flex;
  }

  .Modal__icon {
    position: relative;
    width: 64px;
    height: 64px;
    flex-shrink: 0;
  }

  .Modal__description {
    color: ${({ theme }) => theme.colors.eucalyptus["80"]};
    margin-left: 24px;

    svg {
      width: 12px;
      height: 12px;
      fill: ${({ theme }) => theme.colors.fog["80"]};
    }
  }

  .Modal__contactsList {
    list-style: none;
    display: flex;
    flex-direction: column;
    margin-top: 46px;
  }

  .Modal__contacts {
    max-width: 196px;
    width: 100%;
  }

  .Modal__contactContainer {
    display: flex;
    justify-content: space-between;
  }

  .Modal__contactContainer:not(:last-child) {
    padding-bottom: 16px;
  }

  .Modal__contactContainer:not(:first-child) {
    padding-top: 16px;
    border-top: 1px solid ${({ theme }) => theme.colors.fog["20"]};
  }

  .Modal__contactEmail {
    margin-top: 4px;
    text-decoration: underline;
  }

  @media (max-width: 500px) {
    padding: 24px;
    border-radius: 0;

    .Modal__window {
      padding: 24px;
      width: 100%;
      border-radius: 0 !important;
    }

    .Modal__linkContainer {
      flex-direction: column;
      padding: 16px;
    }

    .Modal__description {
      margin-left: unset;
      margin-top: 24px;
    }

    .Modal__contactContainer {
      flex-direction: column;
    }

    .Modal__contactsList {
      margin-top: 32px;
    }
  }
`;

gql`
  fragment ModalContent on ComponentNestedFooter1Modal {
    title
    subTitle
    linkLabel
    link
    linkDescription
    icon {
      data {
        icon: attributes {
          src: hash
          alt: alternativeText
          ext
        }
      }
    }
    footer1ModalContacts {
      id
      contact
      email
      phoneNumber
    }
  }
`;

export { ModalContactInfo };
